/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2024, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { ApiStatus } from '../../../ts';
import { AIModel } from '../../../ts/aiModels';
import {
  MilvusCollections,
  MilvusDataSources,
  MilvusFields,
  MilvusIndices
} from '../../../ts/milvus';
import type { RootState } from '../../../ts/state';
import {
  CONFIDENCE_THRESHOLD_VALUE,
  RESPONSE_LENGTH_VALUE
} from '../../discovery/util/constants';

export const selectIsDirty = (state: RootState): boolean =>
  state.milvus?.isDirty;

export const selectIsEditing = (state: RootState): boolean =>
  state.milvus?.isEditing;

export const selectIsCheckCredsLoading = (state: RootState): boolean =>
  state.milvus?.checkCredsStatus === ApiStatus.LOADING;

export const selectCheckCredsStatus = (state: RootState): ApiStatus =>
  state.milvus?.checkCredsStatus;

export const selectDataSourcesLoading = (state: RootState): boolean =>
  state.milvus?.dataSourcesStatus === ApiStatus.LOADING;

export const selectCollectionsLoading = (state: RootState): boolean =>
  state.milvus?.collectionsStatus === ApiStatus.LOADING;

export const selectGetIndicesLoading = (state: RootState): boolean =>
  state.milvus?.getIndicesStatus === ApiStatus.LOADING;

export const selectFieldsLoading = (state: RootState): boolean =>
  state.milvus?.fieldsStatus === ApiStatus.LOADING;

export const selectGetModelsLoading = (state: RootState): boolean =>
  state.milvus?.getModelsStatus === ApiStatus.LOADING;

export const selectDataSources = (state: RootState): MilvusDataSources =>
  state.milvus?.dataSources;
export const selectCollections = (state: RootState): MilvusCollections =>
  state.milvus?.collections;

export const selectIndices = (state: RootState): MilvusIndices =>
  state.milvus?.indices;

export const selectFields = (state: RootState): MilvusFields[] =>
  state.milvus?.fields;

export const selectModels = (state: RootState): AIModel[] =>
  state.milvus?.models;

export const selectDraftDatabase = (state: RootState): string =>
  state.milvus?.draft?.search_settings?.milvus?.database;

export const selectDraftCollection = (state: RootState): string =>
  state.milvus?.draft?.search_settings?.milvus?.collection;

export const selectDraftIndex = (state: RootState): string =>
  state.milvus?.draft?.search_settings?.milvus?.index;

export const selectDraftModelId = (state: RootState): string =>
  state.milvus?.draft?.search_settings?.milvus?.embeddings_model_id;

export const selectDraftSkillId = (state: RootState): string =>
  state.milvus?.draft?.skill_id;

export const selectDraftSearchSkill = (state: RootState) => state.milvus?.draft;

export const selectDraftSuccessMessage = (state: RootState): string =>
  state.milvus?.draft?.search_settings?.messages?.success ?? '';

export const selectDraftNoResultsMessage = (state: RootState): string =>
  state.milvus?.draft?.search_settings?.messages?.no_result ?? '';

export const selectDraftConnectivityMessage = (state: RootState): string =>
  state.milvus?.draft?.search_settings?.messages?.error ?? '';

export const selectDraftTitleOptionValue = (state: RootState) =>
  state.milvus?.draft?.search_settings?.schema_mapping?.title ?? '';

export const selectDraftBodyOptionValue = (state: RootState) =>
  state.milvus?.draft?.search_settings?.schema_mapping?.body ?? '';

export const selectDraftUrlOptionValue = (state: RootState) =>
  state.milvus?.draft?.search_settings?.schema_mapping?.url ?? '';

export const selectDraftFilterOptionValue = (state: RootState) =>
  state.milvus?.draft?.search_settings?.milvus?.filter ?? '';

export const selectDraftCitationsTitleValue = (state: RootState) =>
  state.milvus?.draft?.search_settings?.conversational_search
    ?.citations_title ?? '';

export const selectDraftMaxCitationsValue = (state: RootState) =>
  state.milvus?.draft?.search_settings?.conversational_search?.citations
    ?.max_citations ?? -1;

export const selectDraftIsConversationalSearchEnabled = (
  state: RootState
): boolean =>
  state.milvus?.draft?.search_settings?.conversational_search?.enabled ?? false;

export const selectDraftConversationalSearchRetrievalConfidenceThreshold = (
  state: RootState
) =>
  state.milvus?.draft?.search_settings?.conversational_search?.search_confidence
    ?.pre_gen_threshold ?? CONFIDENCE_THRESHOLD_VALUE.LESS_OFTEN;

export const selectDraftConversationalSearchConfidenceThreshold = (
  state: RootState
) =>
  state.milvus?.draft?.search_settings?.conversational_search?.search_confidence
    ?.threshold ?? CONFIDENCE_THRESHOLD_VALUE.LESS_OFTEN;

export const selectDraftConversationalSearchResponseConfidenceThreshold = (
  state: RootState
) =>
  state.milvus?.draft?.search_settings?.conversational_search?.search_confidence
    ?.post_gen_threshold ?? CONFIDENCE_THRESHOLD_VALUE.LESS_OFTEN;

export const selectDraftConversationalSearchMultiTurn = (state: RootState) =>
  state.milvus?.draft?.search_settings?.conversational_search?.multi_turn
    ?.enabled ?? false;

export const selectDraftConversationalSearchVerbosityOption = (
  state: RootState
) =>
  state.milvus?.draft?.search_settings?.conversational_search?.response_length
    ?.option ?? RESPONSE_LENGTH_VALUE.MODERATE;

export const selectDraftUrl = (state: RootState) => {
  return state.milvus?.draft?.search_settings?.milvus?.url ?? '';
};

export const selectDraftPort = (state: RootState) =>
  state.milvus?.draft?.search_settings?.milvus?.port ?? '';

export const selectDraftUser = (state: RootState) => {
  return state.milvus?.draft?.search_settings?.milvus?.username ?? '';
};
export const selectDraftPass = (state: RootState) =>
  state.milvus?.draft?.search_settings?.milvus?.password ?? '';

export const selectAuthType = (state: RootState) =>
  state.milvus?.authType ?? '';

export const selectIsCreateLoading = (state: RootState) => {
  return state.milvus?.createStatus === ApiStatus.LOADING;
};
export const selectCreateError = (state: RootState) => {
  return state.milvus?.createError;
};

// Will be set to false if there are any validation errors when configuring the elastic setup.
export const selectIsSetupValid = (state: RootState) =>
  state.milvus?.isSetupValid ?? false;
